import { Bindable, NotifyProperty, nameof } from "bindable-data";
import { IFortuneWheelModel } from "./IFortuneWheelModel";
import { inject, injectable } from "@launchtray/tsyringe-async";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { DailyBonusWheelWedgeDto } from "by/gamefactory/cityversetycoon/networking/data/responses/dailywheel/DailyBonusWheelWedgeDto";
import { WedgeRewardType } from "./WedgeRewardType";
import { WedgeRewardData as WedgeRewardData } from "./WedgeRewardData";
import { Ticker } from "pixi.js";
import { GoldenWheelModel } from "./GoldenWheelModel";
import { IGoldenWheelModel } from "./IGoldenWheelModel";
import { GetHourlyBonusesResultDto } from "../../../../networking/data/responses/hourlybonus/GetHourlyBonusesResultDto";
import { HourlyBonusItemData } from "./HourlyBonusItemData";
import { VariableSaver } from "../../../../../tools/variableSaver/VariableSaver";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IPiggyWheelModel } from "./IPiggyWheelModel";
import { PiggyWheelModel } from "./PiggyWheelModel";
import { Web3Model } from "../../../../../web3/models/Web3Model";
import { IConnectionModel } from "../../../../networking/IConnectionModel";

@injectable()
export class FortuneWheelModel extends Bindable implements IFortuneWheelModel {
    public readonly goldenWheelModel: NotifyProperty<IGoldenWheelModel>;
    public readonly piggyWheelModel: NotifyProperty<IPiggyWheelModel>;
    public readonly spinAvailableIn: NotifyProperty<number>;
    public readonly wedges: NotifyProperty<Array<DailyBonusWheelWedgeDto>>;
    public readonly hourlyBonusItems: NotifyProperty<Array<HourlyBonusItemData>>;
    public readonly rewardWedgeId: NotifyProperty<string>;
    public readonly currentHourlyBonusSection: NotifyProperty<number>;

    private readonly networkClient: INetworkClient;
    private readonly guestModel: IGuestModel;
    private readonly connectionModel: IConnectionModel;
    private readonly web3Model: Web3Model;
    private readonly variableSaver: VariableSaver;
    private readonly logger: ILogger;

    get rewardWedgeIndex(): number {
        return this.wedges.value.findIndex((item) => item.id == this.rewardWedgeId.value);
    }
    private doneTime: boolean;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IGuestModel") guestModel: IGuestModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        web3Model: Web3Model,
        variableSaver: VariableSaver,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.guestModel = guestModel;
        this.connectionModel = connectionModel;
        this.web3Model = web3Model;
        this.variableSaver = variableSaver;
        this.logger = logger;
        this.goldenWheelModel = new NotifyProperty<IGoldenWheelModel>(this, nameof(this, "goldenWheelModel"), new GoldenWheelModel(this.networkClient, this.connectionModel));
        this.piggyWheelModel = new NotifyProperty<IPiggyWheelModel>(this, nameof(this, "piggyWheelModel"), new PiggyWheelModel(this.networkClient));
        this.spinAvailableIn = new NotifyProperty<number>(this, nameof(this, "spinAvailableIn"), 0);
        this.wedges = new NotifyProperty<Array<DailyBonusWheelWedgeDto>>(this, nameof(this, "wedges"), []);
        this.hourlyBonusItems = new NotifyProperty<Array<HourlyBonusItemData>>(this, nameof(this, "hourlyBonusItems"), []);
        this.rewardWedgeId = new NotifyProperty<string>(this, nameof(this, "rewardWedgeId"), "");
        this.currentHourlyBonusSection = new NotifyProperty<number>(this, nameof(this, "currentHourlyBonusSection"), 0);
        this.doneTime = true;
        Ticker.shared.add(this.updateHandler.bind(this));
        this.connectionModel.propertyChanged.add(this.onConnectionModelChanged, this);
        document.addEventListener("visibilitychange", () => {
            if (!document.hidden) {
                if (!this.connectionModel.connected.value) {
                    return;
                }
                this.updateFortuneWheelStatus();
                this.goldenWheelModel.value.loadGoldenWheelStatus();
            }
        });
    }

    private async updateHandler(): Promise<void> {
        if (this.doneTime) {
            return;
        }
        const diff = this.spinAvailableIn.value - Ticker.shared.deltaMS;
        if (diff <= 0) {
            this.doneTime = true;
            this.spinAvailableIn.setValue(0);
            this.updateFortuneWheelStatus();
            this.goldenWheelModel.value.loadGoldenWheelStatus();
        }
        this.spinAvailableIn.setValue(diff);
    }

    private onConnectionModelChanged(model: IConnectionModel, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.goldenWheelModel.value.reset();
                    this.piggyWheelModel.value.reset();
                    this.spinAvailableIn.setValue(0);
                    this.wedges.setValue([]);
                    this.hourlyBonusItems.setValue([]);
                    this.rewardWedgeId.setValue("");
                    this.currentHourlyBonusSection.setValue(0);
                }
                break;
        }
    }

    public async updateFortuneWheelStatus(): Promise<void> {
        if (!this.connectionModel.connected.value) {
            return;
        }
        await this.loadHourlyBonusStatus();
    }

    private async loadHourlyBonusStatus(): Promise<void> {
        const hourlyBonusStatusDto = await this.networkClient.getHourlyBonusStatus();
        this.validateHourlyBonusDto(hourlyBonusStatusDto);
        this.updateValuesFromHourlyBonusDto(hourlyBonusStatusDto);
    }

    public async claimHourlyBonusReward(): Promise<void> {
        this.checkFirstClaim();
        const claimHourlyBonusResultDto = await this.networkClient.claimHourlyBonusReward();
        if (claimHourlyBonusResultDto.availableIn == null) {
            throw new Error("Failed to claim daily bonus wheel reward with spinAvailableIn.");
        }
        this.spinAvailableIn.setValue(claimHourlyBonusResultDto.availableIn);
        this.doneTime = claimHourlyBonusResultDto.availableIn == undefined || claimHourlyBonusResultDto.availableIn === 0;
        this.rewardWedgeId.setValue(claimHourlyBonusResultDto.rewardWedgeId || "");
    }

    private checkFirstClaim(): void {
        this.hourlyBonusItems.value[this.currentHourlyBonusSection.value].type == "WHEEL" ? this.checkFirstWheelClaim() : this.checkFirstDicesClaim();
    }

    private checkFirstWheelClaim(): void {
        if (!this.variableSaver.exist(`${"WasFirstWheelClaim"},${this.guestModel.id.value}`)) {
            this.variableSaver.saveBoolean(`${"WasFirstWheelClaim"},${this.guestModel.id.value}`, true);
        }
    }

    private checkFirstDicesClaim(): void {
        if (!this.variableSaver.exist(`${"WasFirstDicesClaim"},${this.guestModel.id.value}`)) {
            this.variableSaver.saveBoolean(`${"WasFirstDicesClaim"},${this.guestModel.id.value}`, true);
        }
    }

    private validateHourlyBonusDto(hourlyBonusStatusDto: GetHourlyBonusesResultDto): void {
        if (hourlyBonusStatusDto.items == undefined || hourlyBonusStatusDto.items.length === 0) {
            throw new Error("Failed to create hourly bonus items.");
        }
        hourlyBonusStatusDto.items.forEach(dto => {
            if (dto.id == undefined || dto.id == "") {
                throw new Error("Failed to create hourly bonus item with id.");
            }
            if (dto.type == undefined || dto.type == "") {
                throw new Error("Failed to create hourly bonus item with type.");
            }
        });
    }

    private updateValuesFromHourlyBonusDto(hourlyBonusStatusDto: GetHourlyBonusesResultDto): void {
        this.wedges.setValue(hourlyBonusStatusDto.items.find(dto => dto.wedges != undefined)?.wedges || []);
        this.spinAvailableIn.setValue(hourlyBonusStatusDto.items.find(dto => dto.availableIn != undefined)?.availableIn || 0);
        this.currentHourlyBonusSection.setValue(hourlyBonusStatusDto.items.findIndex(dto => dto.availableIn != undefined) || 0);
        const newHourlyBonusItems: Array<HourlyBonusItemData> = [];
        hourlyBonusStatusDto.items.forEach((item, index) => {
            newHourlyBonusItems.push(new HourlyBonusItemData(index, item.id, item.type, item.freeDiceRollsReward || 0));
        })
        this.hourlyBonusItems.setValue(newHourlyBonusItems);
        this.doneTime = this.spinAvailableIn.value == undefined || this.spinAvailableIn.value === 0;
    }

    public getWedgeRewardData(rewardDto: DailyBonusWheelWedgeDto): WedgeRewardData {
        if (rewardDto.reward.coins != undefined && rewardDto.reward.coins > 0) {
            return new WedgeRewardData(WedgeRewardType.Coin, rewardDto.reward.coins);
        }
        if (rewardDto.reward.shields != undefined && rewardDto.reward.shields > 0) {
            return new WedgeRewardData(WedgeRewardType.Shield, rewardDto.reward.shields);
        }
        if (rewardDto.reward.diceRolls != undefined && rewardDto.reward.diceRolls > 0) {
            return new WedgeRewardData(WedgeRewardType.DiceRoll, rewardDto.reward.diceRolls);
        }
    }
}
